import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCommunity } from '@/contexts/CommunityContext';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Menu, X, User, LayoutDashboard, MessageCircle, Bell, Store, ArrowLeftRight } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import SearchBar from './SearchBar';
import { useNotifications } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useBusiness } from '@/contexts/BusinessContext';
import { getUserDisplayName, getUserInitials, hasAdminDashboardAccess } from '@/utils/userUtils';
import { PATH_CONSTANTS as PATH } from '@/routes/pathConstants';

interface HeaderProps {
  toggleMenu: () => void;
  isOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({
  toggleMenu,
  isOpen
}) => {
  const { scrollY } = useScroll();
  const { currentCommunity, userRole } = useCommunity();
  const { user, signOut, userProfile } = useAuth();
  const { unreadCount: totalUnreadCount } = useNotifications();
  const { t } = useTranslation();
  const { isBusinessOwner } = useBusiness();
  
  const navigate = useNavigate();
  const [hasScrolled, setHasScrolled] = useState(false);
  
  const headerOpacity = useTransform(scrollY, [0, 50], [0.9, 1]);
  const headerShadow = useTransform(scrollY, [0, 50], ['0 1px 2px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.08)']);
  
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const [navBarItems] = useState([{
    title: t('header.nav.home'),
    path: '/'
  }, {
    title: t('header.nav.services'),
    path: '/services'
  }, {
    title: t('header.nav.service_request'),
    path: PATH.SERVICE_REQUEST
  }, {
    title: t('header.nav.about'),
    path: '/about'
  }]);
  
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBecomeProvider = () => {
    if (!user) {
      navigate('/auth?returnTo=/register-business');
      return;
    }
    if (isBusinessOwner) {
      navigate('/admin/my-business');
    } else {
      navigate('/register-business');
    }
  };
  
  // Modified to ensure business owners don't see the admin dashboard
  const canAccessDashboard = hasAdminDashboardAccess(userRole, isBusinessOwner);
  
  return (
    <motion.header 
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: headerShadow,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }} 
      className={`fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 transition-all duration-300 border-b border-gray-100 ${
        hasScrolled 
          ? currentCommunity.logo_url ? 'py-3' : 'py-2'
          : currentCommunity.logo_url ? 'py-4' : 'py-3'
      }`}
    >
      {/* Main header row */}
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="flex items-center space-x-3">
            {currentCommunity.logo_url ? (
              <img
                src={currentCommunity.logo_url}
                alt={currentCommunity.name || t('home.title')}
                className="h-8 sm:h-10 md:h-12 lg:h-14 max-w-48 object-contain community-logo"
                onError={(e) => {
                  // Hide logo and show fallback text if image fails to load
                  e.currentTarget.style.display = 'none';
                  const fallbackSpan = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallbackSpan) fallbackSpan.style.display = 'inline';
                }}
              />
            ) : null}
            <span 
              className={`text-xl font-bold whitespace-nowrap leading-none align-middle md:text-xl lg:text-xl ${
                currentCommunity.logo_url ? 'hidden' : 'inline'
              }`}
            >
              {currentCommunity.name || t('home.title')}
            </span>
          </Link>
        </div>
        
        
        <nav className="hidden lg:flex items-center justify-center flex-1">
          <ul className="flex space-x-8">
            {navBarItems.map(item => (
              <li key={item.title}>
                <Link 
                  to={item.path} 
                  className="text-gray-800 hover:text-primary font-medium transition-colors relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        
        <div className="flex items-center space-x-4">
          <LanguageSelector />
          
          {/* Become a Provider CTA - Visible when not signed in or not a business owner */}
          {(!user || !isBusinessOwner) && (
            <div className="hidden md:block">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBecomeProvider}
                className="rounded-full px-4 py-2 border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50 transition-all duration-300"
              >
                {t('header.nav.become_provider') || 'Become a Provider'}
              </Button>
            </div>
          )}
          
          {user && (
            <div className="relative">
              <Link to="/conversations" aria-label="Messages">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-9 w-9 relative hover:bg-primary/10 border-gray-200" 
                >
                  <Bell size={18} />
                  {totalUnreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
                    >
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>
            </div>
          )}
          
          {user && (
            <div className="hidden lg:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0 overflow-hidden border hover:ring-2 hover:ring-primary/20">
                    <Avatar className="h-8 w-8">
                      {userProfile?.avatar_url || user.user_metadata?.avatar_url ? (
                        <AvatarImage 
                          src={userProfile?.avatar_url || user.user_metadata?.avatar_url} 
                          alt={getUserDisplayName(user, userProfile)} 
                        />
                      ) : (
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {getUserInitials(user, userProfile)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-1">
                  <DropdownMenuLabel>
                    <div className="font-normal">
                      <div className="font-medium">
                        {getUserDisplayName(user, userProfile)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')} 
                    className="group cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                    <span>{t('header.nav.profile')}</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    onClick={() => navigate('/conversations')} 
                    className="group relative cursor-pointer"
                  >
                    <MessageCircle className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                    <span>{t('header.nav.conversations')}</span>
                    {totalUnreadCount > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="ml-auto h-5 min-w-5 flex items-center justify-center p-0 text-[10px]"
                      >
                        {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    onClick={() => navigate('/my-service-requests')} 
                    className="group cursor-pointer"
                  >
                    <ArrowLeftRight className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                    <span>{t('header.nav.my_service_requests')}</span>
                  </DropdownMenuItem>
                  
                  {/* Add My Business link for business owners */}
                  {isBusinessOwner && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin/my-business')} 
                      className="group cursor-pointer"
                    >
                      <Store className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                      <span>{t('header.nav.my_business') || 'My Business'}</span>
                    </DropdownMenuItem>
                  )}

                  {/* Show "Become a Provider" for users who aren't business owners yet */}
                  {!isBusinessOwner && (
                    <DropdownMenuItem 
                      onClick={handleBecomeProvider}
                      className="group cursor-pointer"
                    >
                      <Store className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                      <span>{t('header.nav.become_provider') || 'Become a Provider'}</span>
                    </DropdownMenuItem>
                  )}
                  
                  {/* Only show Dashboard for admins and reviewers, not business owners */}
                  {canAccessDashboard && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')} 
                      className="group cursor-pointer"
                    >
                      <LayoutDashboard className="mr-2 h-4 w-4 text-gray-700 group-hover:text-gray-900" />
                      <span>{t('header.nav.dashboard')}</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleSignOut} 
                    className="group cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('header.nav.signout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          {!user && (
            <div className="hidden lg:flex items-center space-x-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/auth')} className="rounded-full px-6 py-4 border-gray-300 text-gray-700 hover:text-primary hover:border-primary/30">
                {t('auth.signin')}
              </Button>
              <Button variant="default" size="sm" onClick={() => navigate('/auth?mode=signup')} className="rounded-full px-6 py-4 shadow-sm hover:shadow-md">
                {t('auth.signup')}
              </Button>
            </div>
          )}
          
          {!user && (
            <Button variant="default" onClick={() => navigate('/auth')} className="hidden rounded-full px-4 py-2 shadow-sm hover:shadow-md">
              {t('auth.signin')}
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleMenu} 
            className="lg:hidden p-1.5 rounded-full" 
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>
      
      {/* Mobile search bar row - only shown on mobile/tablet */}
      <div className="lg:hidden container mx-auto mt-3 pb-1">
        <SearchBar 
          className="w-full"
          placeholder={t('home.search.placeholder')}
          variant="mobile"
        />
      </div>
    </motion.header>
  );
};

export default Header;
